import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { ChevronDownIcon, ChevronUpIcon, MapPinIcon } from "@heroicons/react/24/outline";
import { Button } from "../atoms/Button";
import { LocationPicker, type LocationValue } from "../molecules/LocationPicker";
import { PhoneInputWithCountry } from "../molecules/PhoneInputWithCountry";
import type { CreateCustomerData, Customer } from "../../types/models";
import { getApiErrorMessage } from "../../utils/apiError";

const inputBase =
  "w-full min-h-[44px] md:min-h-[40px] px-3 py-2.5 text-base border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-400 dark:focus:border-primary-400 transition-colors";

const textareaBase = `${inputBase} resize-none min-h-[80px]`;

interface FieldWrapperProps {
  label: string;
  id?: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}

function FieldWrapper({ label, id, error, required, children }: FieldWrapperProps) {
  return (
    <div className="w-full">
      <label
        htmlFor={id}
        className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5"
      >
        {label}
        {required && <span className="text-red-500 dark:text-red-400 ms-1">*</span>}
      </label>
      {children}
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400 mt-1.5" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export interface CustomerFormData extends CreateCustomerData {}

interface CustomerFormProps {
  onSubmit: (data: CreateCustomerData) => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
  /** When provided, form is in edit mode (pre-filled, submit label "Update"). */
  initialData?: Customer | null;
}

/**
 * Customer create/edit form with validation.
 * - React Hook Form: name and phone required; phone format flexible (Algeria)
 * - Expandable Location section with LocationPicker (GPS, map, manual lat/lng, location note)
 * - Edit mode: key={editingCustomer.id} forces remount so defaultValues update when switching customers
 */
export function CustomerForm({
  onSubmit,
  onCancel,
  isLoading = false,
  initialData,
}: CustomerFormProps) {
  const { t } = useTranslation("customers");
  const [locationExpanded, setLocationExpanded] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [locationValue, setLocationValue] = useState<LocationValue>(() =>
    initialData?.latitude != null && initialData?.longitude != null
      ? {
          latitude: initialData.latitude,
          longitude: initialData.longitude,
          location_note: initialData.location_note ?? undefined,
        }
      : {}
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CustomerFormData>({
    defaultValues: {
      name: initialData?.name ?? "",
      phone: initialData?.phone ?? "",
      email: initialData?.email ?? "",
      address: initialData?.address ?? "",
      notes: initialData?.notes ?? "",
    },
  });

  const onFormSubmit = async (data: CustomerFormData) => {
    setSubmitError(null);
    const payload: CreateCustomerData = {
      name: data.name.trim(),
      phone: data.phone.trim(),
      email: data.email?.trim() || null,
      address: data.address?.trim() || null,
      notes: data.notes?.trim() || null,
    };
    // Include location when both coordinates present (create) or when in edit mode (to allow clearing)
    if (initialData) {
      payload.latitude = locationValue.latitude ?? null;
      payload.longitude = locationValue.longitude ?? null;
      payload.location_note =
        locationValue.location_note != null && locationValue.location_note.trim()
          ? locationValue.location_note.trim()
          : null;
    } else if (
      locationValue.latitude != null &&
      locationValue.longitude != null
    ) {
      payload.latitude = locationValue.latitude;
      payload.longitude = locationValue.longitude;
      if (locationValue.location_note != null && locationValue.location_note.trim()) {
        payload.location_note = locationValue.location_note.trim();
      }
    }
    try {
      await onSubmit(payload);
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, t("form_err_save_failed")));
    }
  };

  const isEdit = !!initialData;

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="flex flex-col">
      {submitError && (
        <div
          className="sticky top-0 z-10 mb-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2.5 shadow-sm"
          role="alert"
        >
          <p className="text-sm text-red-800 dark:text-red-200">{submitError}</p>
        </div>
      )}
      <div className="space-y-6">
        {/* Contact section */}
        <section className="space-y-4" aria-labelledby="customer-contact-heading">
          <h2
            id="customer-contact-heading"
            className="text-sm font-semibold text-neutral-800 dark:text-neutral-200 uppercase tracking-wider flex items-center gap-2 after:flex-1 after:border-b after:border-neutral-200 dark:after:border-neutral-600"
          >
            {t("form_section_contact")}
          </h2>
          <FieldWrapper id="name" label={t("form_label_name")} error={errors.name?.message} required>
            <input
              id="name"
              type="text"
              {...register("name", { required: t("form_err_name_required") })}
              className={inputBase}
              placeholder={t("form_placeholder_name")}
              autoComplete="name"
            />
          </FieldWrapper>
          <Controller
            name="phone"
            control={control}
            rules={{
              required: t("form_err_phone_required"),
              validate: (v) => {
                const digits = (v ?? "").replace(/\D/g, "");
                if (digits.length < 10) return t("form_err_phone_invalid");
                if (digits.length > 15) return t("form_err_phone_too_long");
                return true;
              },
            }}
            render={({ field }) => (
              <PhoneInputWithCountry
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.phone?.message}
                required
                id="phone"
                placeholder={t("form_placeholder_phone")}
                label={t("form_label_phone")}
              />
            )}
          />
          <FieldWrapper id="email" label={t("form_label_email")} error={errors.email?.message}>
            <input
              id="email"
              type="email"
              {...register("email")}
              className={inputBase}
              placeholder={t("form_placeholder_email")}
              autoComplete="email"
            />
          </FieldWrapper>
        </section>

        {/* Address & notes */}
        <section className="space-y-4" aria-labelledby="customer-address-heading">
          <h2
            id="customer-address-heading"
            className="text-sm font-semibold text-neutral-800 dark:text-neutral-200 uppercase tracking-wider flex items-center gap-2 after:flex-1 after:border-b after:border-neutral-200 dark:after:border-neutral-600"
          >
            {t("form_section_address")}
          </h2>
          <FieldWrapper id="address" label={t("form_label_address")} error={errors.address?.message}>
            <textarea
              id="address"
              {...register("address", { maxLength: 2000 })}
              rows={2}
              className={textareaBase}
              placeholder={t("form_placeholder_address")}
            />
          </FieldWrapper>
          <FieldWrapper id="notes" label={t("form_label_notes")} error={errors.notes?.message}>
            <textarea
              id="notes"
              {...register("notes", { maxLength: 2000 })}
              rows={2}
              className={textareaBase}
              placeholder={t("form_placeholder_notes")}
            />
          </FieldWrapper>
        </section>

        {/* Expandable Location */}
        <section aria-labelledby="customer-location-heading">
          <h2
            id="customer-location-heading"
            className="text-sm font-semibold text-neutral-800 dark:text-neutral-200 uppercase tracking-wider flex items-center gap-2 after:flex-1 after:border-b after:border-neutral-200 dark:after:border-neutral-600 mb-4"
          >
            {t("form_section_location")}
          </h2>
          <div className="rounded-xl border border-neutral-200 dark:border-neutral-600 overflow-hidden bg-neutral-50/50 dark:bg-neutral-800/30">
            <button
              type="button"
              onClick={() => setLocationExpanded((prev) => !prev)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 min-h-[48px] text-left hover:bg-neutral-100/80 dark:hover:bg-neutral-700/50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset rounded-xl"
              aria-expanded={locationExpanded}
              aria-controls="customer-location-content"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
                <MapPinIcon className="w-5 h-5 text-primary-500 dark:text-primary-400 shrink-0" />
                {t("form_location_toggle")}
              </span>
              {locationExpanded ? (
                <ChevronUpIcon className="w-5 h-5 text-neutral-500 dark:text-neutral-400 shrink-0" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-neutral-500 dark:text-neutral-400 shrink-0" />
              )}
            </button>
            <div
              id="customer-location-content"
              role="region"
              className={locationExpanded ? "px-4 pb-4 pt-2 border-t border-neutral-200 dark:border-neutral-600" : "hidden"}
            >
              <LocationPicker value={locationValue} onChange={setLocationValue} />
            </div>
          </div>
        </section>
      </div>

      {/* Actions */}
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-6 mt-6 border-t border-neutral-200 dark:border-neutral-700">
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
            className="w-full sm:w-auto min-h-[44px]"
          >
            {t("form_btn_cancel")}
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          disabled={isLoading}
          className="w-full sm:w-auto min-h-[44px]"
        >
          {isLoading
            ? isEdit
              ? t("form_btn_updating")
              : t("form_btn_saving")
            : isEdit
              ? t("form_btn_update")
              : t("form_btn_save")}
        </Button>
      </div>
    </form>
  );
}
